import { useButton } from '@react-aria/button'
import { useHover } from '@react-aria/interactions'
import { useFocusRing } from '@react-aria/focus'
import { filterDOMProps, mergeProps } from '@react-aria/utils'
import cx from 'classnames'
import Styles from './Button.module.scss'
import { ButtonLinkProps, ButtonProps } from './Types'
import { forwardRef, PropsWithChildren } from 'react'
import { useObjectRef } from 'utils/useObjectRef'
import { Link } from 'react-router-dom'

export const Button = forwardRef<HTMLButtonElement, PropsWithChildren<ButtonProps>>((props, fRef) => {
  const ref = useObjectRef<HTMLButtonElement>(fRef)
  const { children, icon: Icon, ghost, variant = 'secondary', ...finalProps } = props
  const { buttonProps, isPressed } = useButton(props, ref)
  const { hoverProps, isHovered } = useHover(props)
  const { isFocusVisible, focusProps } = useFocusRing()

  return (
    <button
      {...mergeProps(filterDOMProps(finalProps), buttonProps, hoverProps, focusProps)}
      className={cx(Styles.root, Styles[variant], {
        [Styles.disabled]: buttonProps.disabled,
        [Styles.hovered]: isHovered,
        [Styles.pressed]: isPressed,
        [Styles.focused]: isFocusVisible,
        [Styles.ghost]: ghost,
      })}
      ref={ref}
    >
      {Icon && (
        <div className={Styles.icon}>
          <Icon />
        </div>
      )}
      {children && <span className={Styles.children}>{children}</span>}
    </button>
  )
})

export const ButtonLink = forwardRef<HTMLAnchorElement, PropsWithChildren<ButtonLinkProps>>((props, fRef) => {
  const ref = useObjectRef<HTMLAnchorElement>(fRef)
  const { children, icon: Icon, ...finalProps } = props
  const { hoverProps, isHovered } = useHover(props)
  const { isFocusVisible, focusProps } = useFocusRing()

  return (
    <Link
      to={finalProps.to}
      {...mergeProps(filterDOMProps(finalProps), hoverProps, focusProps)}
      className={cx(Styles.root, {
        [Styles.hovered]: isHovered,
        [Styles.focused]: isFocusVisible,
      })}
      ref={ref}
    >
      <div className={Styles.icon}>
        <Icon />
      </div>
      {children && <span className={Styles.children}>{children}</span>}
    </Link>
  )
})
